import { Link, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import {
  type FillModificationBody,
  type FillModificationKind,
  type WhatIfKpisResponse,
  useSimulateTradeWhatIf,
} from "../api/hooks/useSimulateTradeWhatIf";
import { useInstruments } from "../api/hooks/useInstruments";
import { useTrade } from "../api/hooks/useTrade";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatMoney, formatPrice } from "../lib/money";
import { formatHoldDuration } from "../lib/tradeAnalytics";
import { formatFillAction } from "../lib/tradeFormat";

type TradeWhatIfParams = {
  tradeId: string;
};

type DraftModification = {
  rowKey: string;
  kind: FillModificationKind;
  target_fill_id: string;
  target_leg_id: string;
  action: "" | "bto" | "sto" | "btc" | "stc";
  quantity: string;
  price_amount: string;
  executed_at: string;
};

function emptyDraft(kind: FillModificationKind): DraftModification {
  return {
    rowKey: crypto.randomUUID(),
    kind,
    target_fill_id: "",
    target_leg_id: "",
    action: "",
    quantity: "",
    price_amount: "",
    executed_at: "",
  };
}

function draftToBody(draft: DraftModification): FillModificationBody {
  const body: FillModificationBody = { kind: draft.kind };
  if (draft.target_fill_id) body.target_fill_id = draft.target_fill_id;
  if (draft.target_leg_id) body.target_leg_id = draft.target_leg_id;
  if (draft.action) body.action = draft.action;
  if (draft.quantity) body.quantity = draft.quantity;
  if (draft.price_amount) body.price_amount = draft.price_amount;
  if (draft.executed_at) body.executed_at = draft.executed_at;
  return body;
}

/** Drop trailing zeros from a decimal string without float rounding. */
function trimDecimal(value: string): string {
  if (!value.includes(".")) return value;
  const trimmed = value.replace(/0+$/, "").replace(/\.$/, "");
  return trimmed === "" || trimmed === "-" ? "0" : trimmed;
}

function formatExecutedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

interface FillRow {
  fill_id: string;
  leg_id: string;
  legNumber: number;
  symbol: string | null;
  action: string;
  quantity: string;
  price_amount: string;
  price_currency: string;
  executed_at: string;
}

function legLabel(legNumber: number, symbol: string | null): string {
  return symbol === null ? `Leg ${legNumber}` : `Leg ${legNumber} · ${symbol}`;
}

function fillOptionLabel(fill: FillRow): string {
  return `${legLabel(fill.legNumber, fill.symbol)} · ${formatFillAction(
    fill.action,
  )} · ${trimDecimal(fill.quantity)} @ ${formatPrice(fill.price_amount, {
    currency: fill.price_currency,
  })} · ${formatExecutedAt(fill.executed_at)}`;
}

const SELECT_CLASS =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm";

function KpiCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">
        {label}
      </span>
      <span className="text-lg font-medium text-on-surface">{value}</span>
    </div>
  );
}

function KpiPanel({
  title,
  kpis,
}: {
  title: string;
  kpis: WhatIfKpisResponse;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          Status: <span className="font-mono">{kpis.status}</span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <KpiCell
            label="Realized P&L"
            value={formatMoney(kpis.realized_pnl_amount, {
              currency: kpis.currency,
            })}
          />
          <KpiCell
            label="Realized %"
            value={
              kpis.realized_return_pct === null
                ? "—"
                : `${(Number(kpis.realized_return_pct) * 100).toFixed(2)}%`
            }
          />
          <KpiCell
            label="Hold time"
            value={formatHoldDuration(kpis.hold_duration_seconds)}
          />
          <KpiCell
            label="Opening notional"
            value={formatMoney(kpis.opening_notional_amount, {
              currency: kpis.currency,
            })}
          />
          <KpiCell label="Quantity" value={kpis.quantity_total} />
          <KpiCell
            label="R-multiple"
            value={kpis.r_multiple === null ? "—" : `${kpis.r_multiple}R`}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export function TradeWhatIfPage() {
  const { tradeId } = useParams({ strict: false }) as TradeWhatIfParams;
  const { data: trade, isLoading, isError, error } = useTrade(tradeId);
  const { data: instrumentsData } = useInstruments();
  const mutation = useSimulateTradeWhatIf(tradeId);
  const [drafts, setDrafts] = useState<DraftModification[]>([]);

  const symbolById = useMemo(() => {
    const map = new Map<string, string>();
    for (const instrument of instrumentsData?.items ?? []) {
      map.set(instrument.instrument_id, instrument.symbol);
    }
    return map;
  }, [instrumentsData]);

  const allFills = useMemo<FillRow[]>(
    () =>
      trade === undefined
        ? []
        : trade.legs.flatMap((leg, legIndex) =>
            leg.fills.map((fill) => ({
              fill_id: fill.fill_id,
              leg_id: leg.leg_id,
              legNumber: legIndex + 1,
              symbol: symbolById.get(leg.instrument_id) ?? null,
              action: fill.action,
              quantity: fill.quantity,
              price_amount: fill.price_amount,
              price_currency: fill.price_currency,
              executed_at: fill.executed_at,
            })),
          ),
    [trade, symbolById],
  );

  if (isLoading) {
    return (
      <section className="p-6" role="status" aria-live="polite">
        <p className="text-on-surface-variant">Loading trade…</p>
      </section>
    );
  }
  if (isError || trade === undefined) {
    return (
      <section className="p-6" role="alert">
        <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
          Trade not found
        </h1>
        <p className="mt-2 text-on-surface-variant">
          {error instanceof Error
            ? error.message
            : "The trade could not be loaded."}
        </p>
        <Link to="/trades" className="mt-4 inline-block text-primary underline">
          Back to trades
        </Link>
      </section>
    );
  }

  const handleAddRow = (kind: FillModificationKind) => {
    setDrafts((prev) => [...prev, emptyDraft(kind)]);
  };

  const handleRemoveRow = (rowKey: string) => {
    setDrafts((prev) => prev.filter((d) => d.rowKey !== rowKey));
  };

  const handleUpdateRow = (
    rowKey: string,
    patch: Partial<DraftModification>,
  ) => {
    setDrafts((prev) =>
      prev.map((d) => (d.rowKey === rowKey ? { ...d, ...patch } : d)),
    );
  };

  const handleSimulate = () => {
    mutation.mutate({ modifications: drafts.map(draftToBody) });
  };

  return (
    <section className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
            What If
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            {trade.status}
            {trade.opened_at
              ? ` · opened ${formatExecutedAt(trade.opened_at)}`
              : ""}
          </p>
        </div>
        <Link
          to="/trades/$tradeId"
          params={{ tradeId: trade.trade_id }}
          className="text-primary underline"
        >
          Back to trade
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Original fills</CardTitle>
          <CardDescription>
            The persisted timeline for this trade, for reference.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {allFills.length === 0 ? (
            <p className="text-sm text-on-surface-variant">
              This trade has no fills.
            </p>
          ) : (
            <table className="w-full text-sm" data-testid="whatif-original-fills">
              <thead>
                <tr className="text-left text-on-surface-variant">
                  <th className="pb-2 font-medium">Leg</th>
                  <th className="pb-2 font-medium">Action</th>
                  <th className="pb-2 font-medium">Qty</th>
                  <th className="pb-2 font-medium">Price</th>
                  <th className="pb-2 font-medium">Date / Time</th>
                </tr>
              </thead>
              <tbody>
                {allFills.map((fill) => (
                  <tr key={fill.fill_id}>
                    <td className="py-1">{legLabel(fill.legNumber, fill.symbol)}</td>
                    <td className="py-1">{formatFillAction(fill.action)}</td>
                    <td className="py-1">{trimDecimal(fill.quantity)}</td>
                    <td className="py-1">
                      {formatPrice(fill.price_amount, {
                        currency: fill.price_currency,
                      })}
                    </td>
                    <td className="py-1">{formatExecutedAt(fill.executed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Modifications</CardTitle>
          <CardDescription>
            Add a new fill, remove an existing fill, or update a fill's price,
            quantity, action, or date. Pick the target leg or fill from the
            dropdown. Empty fields on an update keep the original value.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {drafts.length === 0 && (
            <p className="text-sm text-on-surface-variant">
              No modifications -- "Run simulation" will return the original
              KPIs unchanged.
            </p>
          )}
          {drafts.map((draft) => (
            <div
              key={draft.rowKey}
              className="grid grid-cols-1 gap-3 rounded-md border border-outline p-4 md:grid-cols-2"
              data-testid="whatif-modification-row"
            >
              <div className="md:col-span-2 flex items-center justify-between gap-3">
                <span className="text-sm font-medium uppercase">
                  {draft.kind}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveRow(draft.rowKey)}
                  aria-label="Drop this modification"
                >
                  Drop
                </Button>
              </div>
              {(draft.kind === "remove" || draft.kind === "update") && (
                <div className="space-y-1 md:col-span-2">
                  <Label htmlFor={`fill-${draft.rowKey}`}>Target fill</Label>
                  <select
                    id={`fill-${draft.rowKey}`}
                    className={SELECT_CLASS}
                    value={draft.target_fill_id}
                    onChange={(e) =>
                      handleUpdateRow(draft.rowKey, {
                        target_fill_id: e.target.value,
                      })
                    }
                  >
                    <option value="">(select a fill)</option>
                    {allFills.map((fill) => (
                      <option key={fill.fill_id} value={fill.fill_id}>
                        {fillOptionLabel(fill)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {draft.kind === "add" && (
                <div className="space-y-1 md:col-span-2">
                  <Label htmlFor={`leg-${draft.rowKey}`}>Target leg</Label>
                  <select
                    id={`leg-${draft.rowKey}`}
                    className={SELECT_CLASS}
                    value={draft.target_leg_id}
                    onChange={(e) =>
                      handleUpdateRow(draft.rowKey, {
                        target_leg_id: e.target.value,
                      })
                    }
                  >
                    <option value="">(select a leg)</option>
                    {trade.legs.map((leg, legIndex) => (
                      <option key={leg.leg_id} value={leg.leg_id}>
                        {legLabel(legIndex + 1, symbolById.get(leg.instrument_id) ?? null)} ·{" "}
                        {leg.direction}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {draft.kind !== "remove" && (
                <>
                  <div className="space-y-1">
                    <Label htmlFor={`action-${draft.rowKey}`}>Action</Label>
                    <select
                      id={`action-${draft.rowKey}`}
                      className={SELECT_CLASS}
                      value={draft.action}
                      onChange={(e) =>
                        handleUpdateRow(draft.rowKey, {
                          action: e.target.value as DraftModification["action"],
                        })
                      }
                    >
                      <option value="">
                        {draft.kind === "update" ? "(keep original)" : "(select)"}
                      </option>
                      <option value="bto">Buy</option>
                      <option value="sto">Short</option>
                      <option value="btc">Cover</option>
                      <option value="stc">Sell</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`qty-${draft.rowKey}`}>Quantity</Label>
                    <Input
                      id={`qty-${draft.rowKey}`}
                      value={draft.quantity}
                      onChange={(e) =>
                        handleUpdateRow(draft.rowKey, {
                          quantity: e.target.value,
                        })
                      }
                      inputMode="decimal"
                      placeholder={
                        draft.kind === "update" ? "(keep original)" : "decimal"
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`price-${draft.rowKey}`}>Price</Label>
                    <Input
                      id={`price-${draft.rowKey}`}
                      value={draft.price_amount}
                      onChange={(e) =>
                        handleUpdateRow(draft.rowKey, {
                          price_amount: e.target.value,
                        })
                      }
                      inputMode="decimal"
                      placeholder={
                        draft.kind === "update" ? "(keep original)" : "decimal"
                      }
                    />
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <Label htmlFor={`at-${draft.rowKey}`}>Date / Time</Label>
                    <Input
                      id={`at-${draft.rowKey}`}
                      value={draft.executed_at}
                      onChange={(e) =>
                        handleUpdateRow(draft.rowKey, {
                          executed_at: e.target.value,
                        })
                      }
                      placeholder={
                        draft.kind === "update"
                          ? "(keep original)"
                          : "2026-01-01T09:30:00+00:00"
                      }
                    />
                  </div>
                </>
              )}
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleAddRow("add")}
            >
              + Add fill
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleAddRow("remove")}
            >
              + Remove fill
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleAddRow("update")}
            >
              + Update fill
            </Button>
            <Button
              type="button"
              onClick={handleSimulate}
              disabled={mutation.isPending}
              className="ml-auto"
            >
              {mutation.isPending ? "Running…" : "Run simulation"}
            </Button>
          </div>
          {mutation.isError && (
            <p className="text-sm text-error" role="alert">
              {mutation.error instanceof Error
                ? mutation.error.message
                : "Simulation failed."}
            </p>
          )}
        </CardContent>
      </Card>

      {mutation.data !== undefined && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="whatif-result">
          <KpiPanel title="Original" kpis={mutation.data.original} />
          <KpiPanel title="Modified" kpis={mutation.data.modified} />
        </div>
      )}
    </section>
  );
}
